import { useParams, Link } from "react-router-dom";
import SEO from "../components/SEO";
import { getCategoryBySlug } from "../data/products";
import { IconArrowRight } from "../components/Icons";
import NotFound from "./NotFound";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? getCategoryBySlug(slug) : undefined;

  if (!category) return <NotFound />;

  return (
    <>
      <SEO
        title={`${category.name} - Products`}
        description={category.description}
        canonical={`https://haptone.com/category/${category.slug}`}
      />

      <section className="relative pt-32 pb-24 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${category.image})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/80 to-primary/50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-accent-light text-sm font-semibold uppercase tracking-widest">
            Product Category
          </span>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
            {category.name}
          </h1>
          <p className="mt-6 text-white/80 text-lg max-w-2xl leading-relaxed">
            {category.description}
          </p>
          <div className="mt-6 text-accent-light text-sm font-medium">
            {category.products.length} products available
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {category.products.map((product) => (
              <Link
                key={product.id}
                to={`/product/${product.slug}`}
                className="group bg-white border border-border rounded-2xl overflow-hidden hover:shadow-2xl hover:shadow-secondary/10 transition-all duration-500 hover:-translate-y-1"
              >
                <div className="relative aspect-square overflow-hidden bg-gray-50">
                  <img
                    src={product.image}
                    alt={product.name}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-contain p-6 transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>

                <div className="p-6">
                  <span className="text-accent text-xs font-semibold uppercase tracking-widest">
                    {product.category}
                  </span>
                  <h3 className="mt-2 text-xl font-bold text-primary group-hover:text-secondary transition-colors duration-200 leading-tight">
                    {product.name}
                  </h3>
                  <p className="mt-3 text-muted text-sm leading-relaxed line-clamp-3">
                    {product.shortDescription}
                  </p>
                  <div className="mt-5 inline-flex items-center gap-2 text-secondary font-semibold text-sm group-hover:gap-3 transition-all duration-300">
                    View Details
                    <IconArrowRight
                      size={16}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
